// Progress tracking - currently only in mobile client
//
// Schemas are authoritative — the TS types are derived via z.infer<>. Consumers
// can import the types for compile-time typing (unchanged API) or the
// *Schema exports for runtime validation of API responses / Firestore reads.

import { z } from 'zod';

// === UserProgress Collection (Firestore) ===
export const PROGRESS_SCHEMA_VERSION = 1;

export const MetricCountsSchema = z.object({
  total: z.number(),
  correct: z.number(),
});

export type MetricCounts = z.infer<typeof MetricCountsSchema>;

export const SubjectMetricsSchema = z.object({
  feed: MetricCountsSchema,
  quiz: MetricCountsSchema,
});

export type SubjectMetrics = z.infer<typeof SubjectMetricsSchema>;

export const UserProgressDocumentSchema = z.object({
  schemaVersion: z.number(),
  feed: MetricCountsSchema,
  quiz: MetricCountsSchema,
  subjects: z.record(z.string(), SubjectMetricsSchema),
  updatedAt: z.string(),
  lastReconciledAt: z.string().optional(),
});

export type UserProgressDocument = z.infer<typeof UserProgressDocumentSchema>;

// === Calculated Progress (API responses) ===
export const CalculatedQuizProgressSchema = z.object({
  quizId: z.string(),
  quizTitle: z.string(),
  totalAttempts: z.number(),
  highestScore: z.number(),
  averageScore: z.number(),
  lastAttemptDate: z.string(),
});

export type CalculatedQuizProgress = z.infer<typeof CalculatedQuizProgressSchema>;

export const CalculatedSubjectProgressSchema = z.object({
  subject: z.string(),
  totalUniqueQuizzesAttempted: z.number(),
  averageOverallQuizScore: z.number(),
  totalQuestionsAttempted: z.number(),
  totalQuestionsCorrect: z.number(),
  percentageCorrect: z.number(),
});

export type CalculatedSubjectProgress = z.infer<typeof CalculatedSubjectProgressSchema>;

// Backend compatibility - same structure as UserProgress but different name
export const UserProgressMetricsSchema = z.object({
  quizProgress: z.array(CalculatedQuizProgressSchema),
  subjectProgress: z.array(CalculatedSubjectProgressSchema),
  totalUniqueQuizzesAttempted: z.number().optional(),
  averageOverallQuizScore: z.number().optional(),
  totalQuestionsAttempted: z.number().optional(),
  totalQuestionsCorrect: z.number().optional(),
});

export type UserProgressMetrics = z.infer<typeof UserProgressMetricsSchema>;

export const RecentQuizAttemptSchema = z.object({
  id: z.string(),
  quizId: z.string(),
  quizTitle: z.string(),
  userId: z.string(),
  score: z.number(),
  createdAt: z.string(),
  subject: z.string(),
});

export type RecentQuizAttempt = z.infer<typeof RecentQuizAttemptSchema>;
